import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, MessageCircle, MapPin, Anchor, Home as HomeIcon } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { media } from "@/i18n/translations";
import SEO from "@/components/SEO";
import { FadeUp } from "@/components/ui-primitives";

const COPY = {
  en: {
    eyebrow: "Off course",
    code: "404",
    title: "The page has drifted off chart",
    lead: "This coordinate isn't on our route. Let us help you reach the right port.",
    coordinates: "42° 01′ 45″ N · 35° 09′ 00″ E — somewhere unplotted",
    backHome: "Return to home",
    contactCta: "Send an ETA",
    quickNav: "Quick navigation",
    links: [
      { to: "/", label: "Home" },
      { to: "/services", label: "Services" },
      { to: "/destinations", label: "Destinations" },
      { to: "/insights", label: "Insights" },
      { to: "/about", label: "About Rota" },
      { to: "/contact", label: "Contact" },
    ],
  },
  ru: {
    eyebrow: "Сбились с курса",
    code: "404",
    title: "Эта страница ушла с карты",
    lead: "Эти координаты не на нашем маршруте. Позвольте вернуть вас в нужный порт.",
    coordinates: "42° 01′ 45″ N · 35° 09′ 00″ E — точка вне карты",
    backHome: "На главную",
    contactCta: "Отправить ETA",
    quickNav: "Быстрая навигация",
    links: [
      { to: "/", label: "Главная" },
      { to: "/services", label: "Услуги" },
      { to: "/destinations", label: "Направления" },
      { to: "/insights", label: "Аналитика" },
      { to: "/about", label: "О компании" },
      { to: "/contact", label: "Контакты" },
    ],
  },
  tr: {
    eyebrow: "Rotadan çıktınız",
    code: "404",
    title: "Aradığınız sayfa rotanın dışına savruldu",
    lead: "Bu koordinat bizim rotamızda değil. Sizi doğru limana götürelim.",
    coordinates: "42° 01′ 45″ K · 35° 09′ 00″ D — haritada yok",
    backHome: "Ana sayfaya dön",
    contactCta: "ETA gönder",
    quickNav: "Hızlı navigasyon",
    links: [
      { to: "/", label: "Ana Sayfa" },
      { to: "/services", label: "Hizmetler" },
      { to: "/destinations", label: "Rotalar" },
      { to: "/insights", label: "Yazılar" },
      { to: "/about", label: "Hakkımızda" },
      { to: "/contact", label: "İletişim" },
    ],
  },
};

const NotFound = () => {
  const { lang } = useLanguage();
  const copy = useMemo(() => COPY[lang] || COPY.en, [lang]);

  return (
    <>
      <SEO
        path="/404"
        title={`${copy.code} · ${copy.title}`}
        description={copy.lead}
      />

      <main
        className="relative min-h-[92vh] overflow-hidden flex items-center"
        data-testid="not-found-page"
      >
        {/* Ambient background */}
        <div className="absolute inset-0">
          <img
            src={media.heroMain}
            alt=""
            className="w-full h-full object-cover kenburns"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-navy/85 via-navy/75 to-navy/90" />
          <div className="absolute inset-0 opacity-[0.08]" style={{
            backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.6) 1px, transparent 1px), radial-gradient(circle at 80% 60%, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "120px 120px, 180px 180px",
          }} />
        </div>

        {/* Decorative compass — top right */}
        <div
          className="hidden md:block absolute top-24 right-16 lg:right-28 w-48 h-48 lg:w-64 lg:h-64 pointer-events-none"
          aria-hidden="true"
        >
          <div className="relative w-full h-full flex items-center justify-center">
            <div className="absolute inset-0 rounded-full border border-brand/30 animate-[spin_80s_linear_infinite]">
              <div className="absolute left-1/2 top-2 -translate-x-1/2 text-[10px] tracking-[0.3em] text-brand/60 font-semibold">N</div>
              <div className="absolute left-1/2 bottom-2 -translate-x-1/2 text-[10px] tracking-[0.3em] text-brand/60 font-semibold">S</div>
              <div className="absolute top-1/2 left-2 -translate-y-1/2 text-[10px] tracking-[0.3em] text-brand/60 font-semibold">W</div>
              <div className="absolute top-1/2 right-2 -translate-y-1/2 text-[10px] tracking-[0.3em] text-brand/60 font-semibold">E</div>
            </div>
            <div className="absolute inset-4 rounded-full border border-brand/20" />
            <div className="absolute inset-8 rounded-full border border-brand/10" />
            <Compass
              size={80}
              className="text-brand/70 animate-[spin_40s_linear_infinite_reverse]"
              strokeWidth={0.75}
            />
          </div>
        </div>

        {/* Content */}
        <div className="relative w-full px-6 sm:px-10 lg:px-16 py-24">
          <div className="max-w-3xl">
            <FadeUp>
              <div className="flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-brand mb-8">
                <Anchor size={14} />
                <span>{copy.eyebrow}</span>
              </div>
            </FadeUp>

            <FadeUp delay={0.05}>
              <div
                className="font-display leading-none text-white/95 select-none"
                style={{ fontSize: "clamp(7rem, 18vw, 15rem)" }}
                data-testid="not-found-code"
              >
                <span className="inline-block relative">
                  4
                  <span className="absolute -top-2 -right-2 w-2 h-2 bg-brand rounded-full hidden sm:block" />
                </span>
                <span className="inline-block mx-2 relative align-middle">
                  <span className="inline-block w-[0.7em] h-[0.7em] rounded-full border-4 md:border-[6px] border-brand align-middle" />
                </span>
                <span>4</span>
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <h1 className="mt-8 font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.15] max-w-2xl">
                {copy.title}
              </h1>
            </FadeUp>

            <FadeUp delay={0.15}>
              <p className="mt-5 text-lg text-white/75 leading-relaxed max-w-xl">
                {copy.lead}
              </p>
            </FadeUp>

            <FadeUp delay={0.2}>
              <div className="mt-5 flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-brand/80 font-mono">
                <MapPin size={12} />
                <span>{copy.coordinates}</span>
              </div>
            </FadeUp>

            {/* CTAs */}
            <FadeUp delay={0.28}>
              <div className="mt-12 flex flex-wrap gap-4">
                <Link
                  to="/"
                  className="group inline-flex items-center gap-3 bg-brand text-white px-7 py-4 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-brand-deep transition-colors"
                  data-testid="not-found-home-btn"
                >
                  <HomeIcon size={14} />
                  {copy.backHome}
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-3 border border-white/30 text-white px-7 py-4 text-[11px] tracking-[0.25em] uppercase font-semibold hover:border-brand hover:text-brand transition-colors"
                  data-testid="not-found-contact-btn"
                >
                  <MessageCircle size={14} />
                  {copy.contactCta}
                </Link>
              </div>
            </FadeUp>

            {/* Quick nav */}
            <FadeUp delay={0.34}>
              <div className="mt-16 pt-10 border-t border-white/15 max-w-xl">
                <div className="text-[10px] tracking-[0.3em] uppercase text-brand/70 mb-5">
                  {copy.quickNav}
                </div>
                <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3" data-testid="not-found-quick-nav">
                  {copy.links.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        className="group inline-flex items-center gap-2 text-white/70 hover:text-brand text-sm"
                      >
                        <span className="w-0 group-hover:w-3 h-px bg-brand transition-all duration-300" />
                        <span>{l.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
          </div>
        </div>
      </main>
    </>
  );
};

export default NotFound;
